import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import Loader from "../Components/ui/Loader";
import { useLocation } from "react-router-dom"; // To track current route
import { useAlert } from "../hooks/useAlert";
import { handleOnOkAlert } from "../utils/helperFunctions";
import {
  baseURL,
  logout as logoutText,
  refresh,
} from "../config/variables";
import { ProjectDataProps, ShowAlertProps } from "../Components/common/types";
import { useLoader } from "../hooks/useLoader";
import { useFetchProjects } from "../hooks/useFetchProjects";
import { errLog, log } from "../utils/logger";

interface AuthContextType {
  accessToken: string | null;
  setAccessToken: (token: string | null) => void;

  refreshAccessToken: () => Promise<string | null>;
  logout: (
    isAutoLogout?: boolean,
    title?: string,
    message?: string
  ) => Promise<void>;
  
  logoutInProgress: boolean; // Expose logoutInProgress state
  isLoggedOut: boolean; //Expose this state
  setIsLoggedOut: React.Dispatch<React.SetStateAction<boolean>>; //Set whether user is logged out or not

  showAlert: ShowAlertProps['showAlert'],
  closeAlert: ()=> void,

  SESSION_KEY: string;

  loggedInUserName: string;
  setLoggedInUserName:React.Dispatch<React.SetStateAction<string>>;

    // 🔽 Add projects-related properties
  isProjectLoading: boolean;
  fetchedProjects: ProjectDataProps[];
  FetchLoader: JSX.Element | null;
  fetchProjects: () => Promise<void>;

}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const SESSION_KEY = "sessionId";
const IS_LOGGED_OUT_KEY = "isLoggedOut";

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [logoutInProgress, setLogoutInProgress] = useState<boolean>(false); // Track logout process state
  const [isLoggedOut, setIsLoggedOut] = useState<boolean>(() => {
    // Initialize isLoggedOut from localStorage, defaulting to true if not found
    const storedIsLoggedOut = localStorage.getItem(IS_LOGGED_OUT_KEY);
    return storedIsLoggedOut ? JSON.parse(storedIsLoggedOut) : true;
  });

  const [isInitialized, setIsInitialized] = useState<boolean>(false); //Track when to refresh access token
  const location = useLocation(); // Track current route

  const MAX_RETRIES = 3; //Limit the number of retries if the token refresh fails
  const retryCount = useRef(0); // Track the number of retries

  const isTokenRefreshInitiated = useRef(false); // Track if refresh function has been initiated, preventing multiple calls of the refreshAccessToken
  const isRefreshingRef = useRef(false); // Track if a refresh is in progress, ensure one refresh instance is running, no re-renders

  const { showAlert, closeAlert, AlertComponent } = useAlert(); //Alert box
  const { setShowLoader, LoaderComponent } = useLoader(); // Loader state
  const { showLoader: isProjectLoading, projects: fetchedProjects, LoaderComponent: FetchLoader, fetchProjects } = useFetchProjects();

  const [loggedInUserName, setLoggedInUserName] = useState("");

  const PRIVATE_ROUTES = [
    "/dashboard/landing",
    "/dashboard/landing/add-project",
    "/dashboard/landing/edit-project",
    "/dashboard/profile",
    "/dashboard/insights-and-analytics",
  ]; // Check if the current route is private
  const isPrivateRoute = PRIVATE_ROUTES.includes(location.pathname);

  //ENSURE IS_LOGGED_OUT_KEY PERSISTS ACROSS STATE
  useEffect(() => {
    // Update localStorage whenever isLoggedOut changes, (persistent state)
    localStorage.setItem(IS_LOGGED_OUT_KEY, JSON.stringify(isLoggedOut));
  }, [isLoggedOut]);

  //LOGOUT FUNCTION
  const logout = useCallback(
    async (isAutoLogout: boolean = false, title?: string, message?: string) => {
      if (isLoggedOut || logoutInProgress) {
        return;
      }

      setShowLoader(true); // Show loader while processing

      try {
        setLogoutInProgress(true); // Mark logout as in progress
        const response = await fetch(baseURL + logoutText, {
          method: "GET",
          credentials: "include", // Include the refresh token cookie
        });

        if (!response.ok) {
          //500 server error
          const errData = await response.json();
          showAlert(true, "Error", errData.message, false);
          return;
        }

        const data = await response.json();

        //Handle successful response
        switch (response.status) {
          case 200:
            setIsLoggedOut(true);
            //auto logout
            if (isAutoLogout) {
              if (title && message) {
                showAlert(
                  true,
                  title || "Session Expired",
                  message || "Your session has expired. Please log in again.",
                  false,
                  () => {
                    setAccessToken(null);
                    return true;
                  }
                );
              }
            } else {
              //manual logout
              showAlert(
                false,
                "Success",
                data.message,
                false,
                undefined,
                undefined,
                false
              );

              setTimeout(() => {
                setAccessToken(null); // Clear the access token in order for RouteGuard to redirect login page
                closeAlert(); //close alert
              }, 2000);
            }

            break;
          case 204:
            //log out user from front end a case of invalid or expired token
            log("Here is 204 in logout");
            setIsLoggedOut(true);
            setAccessToken(null);
            handleOnOkAlert(
              showAlert,
              setAccessToken,
              "Expired Session",
              "This session is no longer active"
            );
            break;
          default:
            //Just in case
            showAlert(
              true,
              "Error",
              "An unexpected error occurred. Please try again.",
              false
            );
        }
      } catch (error) {
        // Handle network, connection refuse to server or unexpected errors, do not log the user from front end
        showAlert(
          true,
          "Error",
          "An unexpected error occured. Please try again later.",
          false
        );
      } finally {
        setShowLoader(false); // Hide loader
        setLogoutInProgress(false); // Reset logout progress
      }
    },
    [showAlert, closeAlert, setShowLoader, isLoggedOut, logoutInProgress]
  );

  //REFRESH ACCESS TOKEN FUNCTION
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    // Prevent refresh during logout process, when logged out or, in public routes or if there a refresh instance
    if (
      isLoggedOut ||
      !isPrivateRoute ||
      logoutInProgress ||
      isRefreshingRef.current
    ) {
      setIsInitialized(true); // If no need to refresh, mark as initialized
      return null;
    }

    isRefreshingRef.current = true; //set isRefreshingRef in progress, this is checked if there is a refresh instance

    try {
      log("Starting refresh access token process");

      // Simulate a network error
      // throw new Error("Network error");

      const response = await fetch(baseURL + refresh, {
        method: "GET",
        credentials: "include", // Include the refresh token cookie
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Handle different error statuses
        if (response.status === 401) {
          // Session expired due lack of cookie, leading to missing token, silently redirect to login, don't call logout since it won't log out user,
          // instead rely on refresh controller from backend to clear the refresh token hence that will be considered logout
          setIsLoggedOut(true);
          setAccessToken(null);
          showAlert(true, "Session Expired", "Please log in again.", false);
        } else if (response.status === 403) {
          // Security issue (e.g., invalid or tampered refresh token, failed verification), logout from front end
          setAccessToken(null);
          setIsLoggedOut(true);
          showAlert(
            true,
            "Security Alert",
            "Your session is invalid. Please log in again.",
            false
          );
        } else if (response.status === 500) {
          // Server error
          if (logoutInProgress === false && isPrivateRoute && !isLoggedOut) {
            if (retryCount.current < MAX_RETRIES) {
              retryCount.current++; // Increment retry count
              log("Retry count for 500 status", retryCount.current);
              setTimeout(refreshAccessToken, 5000); // Retry after 5 seconds
            } else {
              showAlert(
                true,
                "Error",
                "Something went wrong on our end. Kindly wait as we sort the issue.",
                false
              );
            }
          }
        } else {
          // Generic fallback for unexpected errors from backend
          showAlert(
            true,
            "Error",
            errorData.message || "Unknown error occurred.",
            false
          );
        }

        return null; //stop further excecution
      }

      const data = await response.json();
      retryCount.current = 0; //Reset retry count on success
      setAccessToken(data.accessToken);
      return data.accessToken; //Return the new token
    } catch (error) {
      errLog("Error refreshing token:", error);
      if (logoutInProgress === false && isPrivateRoute && !isLoggedOut) {
        retryCount.current++; // Increment retry count
        log("Show trials: ", retryCount.current);
        if (retryCount.current < MAX_RETRIES) {
          setTimeout(refreshAccessToken, 5000); // Retry after 5 seconds
        } else {
          log("Max retries reached. Logging out...");
          logout(true, "Error", "Unexpected error occured"); // Auto-logout, will run since at this point,
          // error is not related to invalid or expired sessions,
          // more like front end issue, but for a connection refused or network error, logout can hanlde this gracefully
        }
      }
      return null; // Return null if an error occurs
    } finally {
      isRefreshingRef.current = false; // Reset isRefreshingRef
      setIsInitialized(true); // Mark auth as initialized, no need to refresh
    }
  }, [logoutInProgress, isPrivateRoute, showAlert, logout, isLoggedOut]);

  // INTITIALIZE ACCESS TOKEN ON PAGE RELOAD OR DUPLICATE
  useEffect(() => {
    if (!isInitialized && !isTokenRefreshInitiated.current) {
      isTokenRefreshInitiated.current = true; // Mark refresh as initiated, prevents multiple calls of refreshAccessToken
      refreshAccessToken();
    }
  }, [refreshAccessToken, isInitialized]);

  // // Handle session check
  // useEffect(() => {
  //   const checkSession = async () => {
  //     const localSessionId = localStorage.getItem(SESSION_KEY);
  //     try {
  //       const response = await fetch(baseURL + validateSession, {
  //         method: "GET",
  //         credentials: "include", // Include the refresh token cookie
  //       });

  //       //if !respone.ok, null id is returned
      

  //       const data = await response.json();
  //       log("Data", data);
  //       log("LocalSessionId", localSessionId, "LatestSessionId", data.sessionId);
  //       if (localSessionId && data.sessionId !== localSessionId && !isLoggedOut) {
  //         log("Logging out due to session conflict...");
  //         logout(
  //           true,
  //           "Session Conflict",
  //           "You have been logged out due to session conflict"
  //         );
  //         // localStorage.removeItem(SESSION_KEY);
  //       }
  //     } catch (error) {
  //       log("Session validation failed:", error);
  //       logout(
  //         true,
  //         "Error",
  //         "You have been logged out due to unexpected error"
  //       );
  //     }
  //   };

  //   // Listen for session updates in other tabs
  //   const handleStorageChange = (event: StorageEvent) => {
  //     if (event.key === SESSION_KEY) {
  //       checkSession(); // Check session when session ID changes
  //     }
  //   };

  //   window.addEventListener("storage", handleStorageChange);
  //   return () => window.removeEventListener("storage", handleStorageChange);
  // }, [isLoggedOut, logout]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        setAccessToken,
        refreshAccessToken,
        logout,
        logoutInProgress,
        isLoggedOut,
        setIsLoggedOut,
        showAlert,
        closeAlert,
        SESSION_KEY,
        loggedInUserName,
        setLoggedInUserName,

         // ✅ Add project-related values here
        isProjectLoading,
        fetchedProjects,
        FetchLoader,
        fetchProjects,
      }}
    >
      {!isInitialized ? <Loader /> : children}

      {/* Call smart alert component here*/}
      {AlertComponent}

      {/* Show Loader while submitting */}
      {LoaderComponent}
    </AuthContext.Provider>
  );
};
